import { AssignBodyType, typedAxios, useReqParams } from './apiConfig';
import { Api } from '../types/apiSpec';
import { isCourseType } from '../utils/trainings/typesChecking';
import PutLibraryEntryRequestBody = Api.PutLibraryEntryRequestBody;

export const useJoinTeam = () => {
  const reqParams = useReqParams({
    url: '/teams/{teamid}/join',
  });

  return (teamid: string) =>
    typedAxios({
      ...reqParams,
      method: 'post',
      params: { teamid },
    });
};

export const useAssignUserToTraining = (type: Api.TrainingType) => {
  const reqParams = useReqParams({
    url: `/users/{userid}/${isCourseType(type) ? 'courses/{courseid}' : 'learningpaths/{lpId}'}`,
  });

  return (userid: string, trainingId: string) =>
    typedAxios({
      ...reqParams,
      method: 'post',
      params: { userid, courseid: trainingId, lpId: trainingId },
    });
};
export const useAssignTeamToTraining = (type: Api.TrainingType, trainingId: string) => {
  const reqParams = useReqParams({
    url: `/teams/{teamid}/${isCourseType(type) ? 'courses/{courseid}' : 'learningpaths/{lpId}'}`,
  });

  return (teamid: string) =>
    typedAxios({
      ...reqParams,
      method: 'post',
      params: { teamid, courseid: trainingId, lpId: trainingId },
    });
};

export const useAssignUserToTeam = (teamid: string) => {
  const reqParams = useReqParams({
    url: '/teams/{teamid}/users/{userid}',
  });

  return (userid: string) =>
    typedAxios({
      ...reqParams,
      method: 'post',
      params: { teamid, userid },
    });
};

export const useCreateSubteam = (teamid: string) => {
  const reqParams = useReqParams({
    url: '/teams/{teamid}/teams',
  });

  return (name: string) =>
    typedAxios({
      ...reqParams,
      method: 'post',
      params: { teamid },
      body: { name },
    });
};

export const useRenameTeam = (teamid: string) => {
  const reqParams = useReqParams({
    url: '/teams/{teamid}',
  });

  return (name: string) =>
    typedAxios({
      ...reqParams,
      method: 'patch',
      params: { teamid },
      body: { id: teamid, name },
    });
};

export const useDeleteTeam = () => {
  const reqParams = useReqParams({
    url: '/teams/{teamid}',
  });

  return (teamid: string) =>
    typedAxios({
      ...reqParams,
      method: 'delete',
      params: { teamid },
    });
};

export const useDeleteUserFromCourse = () => {
  const reqParams = useReqParams({
    url: '/users/{userid}/courses/{courseid}',
  });

  return ({ userid, courseid }: { userid: string; courseid: string }) =>
    typedAxios({
      ...reqParams,
      method: 'delete',
      params: { courseid, userid },
    });
};

export const useDeleteUserFromLp = () => {
  const reqParams = useReqParams({
    url: '/users/{userid}/learningpaths/{lpId}',
  });

  return ({ userid, lpId }: { userid: string; lpId: string }) =>
    typedAxios({
      ...reqParams,
      method: 'delete',
      params: { lpId, userid },
    });
};

export const useDeleteUserFromTraining = (type: Api.TrainingType) =>
  isCourseType(type) ? useDeleteUserFromCourse() : useDeleteUserFromLp();

export const useDeleteTeamFromTraining = (trainingid: string, type: Api.TrainingType) => {
  const reqParams = useReqParams({
    url: isCourseType(type) ? '/teams/{teamid}/courses/{courseid}' : '/teams/{teamid}/learningpaths/{lpId}',
  });

  return (teamid: string) =>
    typedAxios({
      ...reqParams,
      method: 'delete',
      params: { courseid: trainingid, lpId: trainingid, teamid },
    });
};

export const useUserAssignCourse = (userid: string) => {
  const reqParams = useReqParams({
    url: '/users/{userid}/courses',
  });

  return (body: { id: string }[]) =>
    typedAxios({
      ...reqParams,
      method: 'post',
      params: { userid },
      body,
    });
};

export const useUserAssignLp = (userid: string) => {
  const reqParams = useReqParams({
    url: '/users/{userid}/learningpaths',
  });

  return (body: AssignBodyType) =>
    typedAxios({
      ...reqParams,
      method: 'post',
      params: { userid },
      body,
    });
};

export const useAssignTrainingToTeam = ({ teamid, type }: { teamid: string; type: Api.TrainingType }) => {
  const reqParams = useReqParams({
    url: isCourseType(type) ? '/teams/{teamid}/courses' : '/teams/{teamid}/learningpaths',
  });

  return (body: AssignBodyType) =>
    typedAxios({
      ...reqParams,
      method: 'post',
      params: { teamid },
      body,
    });
};

export const useSetUserPreference = (userid: string) => {
  const reqParams = useReqParams({
    url: '/users/{userId}/preferences/{preferenceType}/{id}',
  });

  return (params: { preferenceType: Api.Preference; id: string }) =>
    typedAxios({
      ...reqParams,
      method: 'put',
      params: { ...params, userid },
    });
};

export const useDeleteUserPreference = (userid: string) => {
  const reqParams = useReqParams({
    url: '/users/{userId}/preferences/{preferenceType}/{id}',
  });

  return (params: { preferenceType: Api.Preference; id: string }) =>
    typedAxios({
      ...reqParams,
      method: 'delete',
      params: { ...params, userid },
    });
};

export const useAssignEntry = ({ id, type }: { type: Api.TrainingType; id: string }) => {
  const reqParams = useReqParams({
    url: isCourseType(type) ? '/courses/{courseid}/library/entries' : '/learningpaths/{lpId}/library/entries',
  });

  return (body: PutLibraryEntryRequestBody) =>
    typedAxios({
      ...reqParams,
      method: 'put',
      params: { ...(isCourseType(type) ? { courseid: id } : { lpId: id }) },
      body,
    });
};
